
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import { DataModules } from "@/store/modules/data";
import {
  IProvisionRequestProviderData,
  IProvisionRequestProviderMethods
} from "@/models/providers/provisionRequests";
import { IProvider } from "@/models/providers";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import { IProvisionRequest } from "@/models/provisioning";
import { ProvisionRequestStatusCodes as PRSC } from "@/data/enums/contractProducts";

@Component({
  computed: {
    ...mapState({
      request(state, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.PROVISIONING_REQUESTS,
          scope: this.requestScope
        });
      },
      requestScope(state, getters) {
        return getters[`data/${DataModules.PROVISIONING_REQUESTS}/singleScope`](
          this.requestId
        );
      }
    })
  }
})
export default class ProvisionRequestProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Props

  @Prop({ required: true })
  public readonly requestId!: IProvisionRequest["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: () => () => ({}) })
  public onNotFound!: IProvider["onNotFound"];

  // Data

  public readonly request!: IProvisionRequest;
  public readonly requestScope!: string;
  public isLoading = true;
  public isReloading = false;
  public isProcessing = {};
  public hasRequestError = false;

  // Computed

  public get canCancel() {
    if (!this.$userCan("cancel_provision_request")) return false;
    return [
      PRSC.PROVISION_REQUEST_PENDING,
      PRSC.PROVISION_REQUEST_DRAFT,
      PRSC.PROVISION_REQUEST_SCHEDULED,
      PRSC.PROVISION_REQUEST_DISPATCHED
    ].includes(this.request?.status?.code as PRSC);
  }

  public get mappedStatus() {
    switch (this.request?.status?.code) {
      case PRSC.PROVISION_REQUEST_DRAFT:
        return {
          message: this.$t("_sentence.provisioning.request_draft_msg"),
          props: {
            icon: "drafting-compass",
            type: "is-dark"
          }
        };
      case PRSC.PROVISION_REQUEST_SCHEDULED:
        return {
          message: this.$t("_sentence.provisioning.request_scheduled_msg"),
          props: {
            icon: "clock",
            type: "is-caution"
          }
        };
      case PRSC.PROVISION_REQUEST_PENDING:
        return {
          message: this.$t("_sentence.provisioning.request_pending_msg"),
          props: {
            icon: "hourglass-half",
            type: "is-caution"
          }
        };
      case PRSC.PROVISION_REQUEST_DISPATCHED:
        return {
          message: this.$t("_sentence.provisioning.request_dispatched_msg"),
          props: {
            icon: "paper-plane",
            type: "is-info"
          }
        };
      case PRSC.PROVISION_REQUEST_CANCELLED:
        return {
          message: this.$t("_sentence.provisioning.request_cancelled_msg"),
          props: {
            icon: "times-circle",
            type: "is-grey"
          }
        };
      case PRSC.PROVISION_REQUEST_ERROR:
        return {
          message: this.request?.result_message,
          props: {
            icon: "exclamation-triangle",
            type: "is-danger"
          }
        };
      case PRSC.PROVISION_REQUEST_SUCCESS:
        return {
          message: this.request?.result_message,
          props: {
            icon: "check-circle",
            type: "is-success"
          }
        };
      default:
        return undefined;
    }
  }

  // Methods

  public async loadData({ ignoreStored = false } = {}) {
    return this.getProvisionRequest(ignoreStored);
  }

  public async getProvisionRequest(ignoreStored = false): Promise<any> {
    try {
      await this.$store.dispatch(
        `data/${DataModules.PROVISIONING_REQUESTS}/get`,
        {
          requestId: this.requestId,
          payload: {
            ignoreStored,
            scope: this.requestScope,
            vm: this
          }
        }
      );
      this.hasRequestError = false;
    } catch (error) {
      const is403 = this.$store.getters["api/is403"](error);
      const is404 = this.$store.getters["api/is404"](error);
      if ((is403 || is404) && this.onNotFound) this.onNotFound(this);
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData({ ignoreStored: true });
  }

  public async openCancelRequestModal({ onSuccess = () => ({}) }) {
    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/openCancelRequestModal`,
      {
        requestId: this.requestId,
        onSuccess: async () => {
          this.$bus.$emit("refreshRequestTable");
          onSuccess();
        }
      }
    );
  }

  // Life-cycle Methods

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $requestData: this.providerData(),
        $requestMethods: this.providerMethods
      })
    );
  }

  public created() {
    this.isLoading = true;
    this.loadData();
  }

  public beforeDestroy() {
    // Bin provision request data
    this.$store.dispatch("data/binSingle", {
      scope: this.requestScope,
      storeModule: DataModules.PROVISIONING_REQUESTS,
      vm: this
    });
  }

  // Provide

  @Provide("provisionRequestProvider")
  public providerData(): IProvisionRequestProviderData {
    return {
      canCancel: this.canCancel,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      mappedStatus: this.mappedStatus,
      request: this.request,
      requestId: this.requestId
    };
  }

  @Provide("provisionRequestProviderMethods")
  public get providerMethods(): IProvisionRequestProviderMethods {
    return {
      reloadData: this.reloadData,
      openCancelRequestModal: this.openCancelRequestModal
    };
  }
}
